import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useDispatch } from "react-redux";
import { addEditEntertainment, getOneEntertainment, entertainmentImageUpload } from "../../Redux/Slices/entertainmentSlice";
import toast from "react-hot-toast";

const AddEditEntertainment = ({ isOpen, onClose, rowData }) => {

    const [isProfileUploading, setIsProfileUploading] = useState(false);
    const today = new Date().toISOString().split('T')[0];
    const [initialValues, setInitialValues] = useState({
        banner: "",
        fromDate: today,
        toDate: "",
        files: []
    });
    const dispatch = useDispatch();
    const [selectedImages, setSelectedImages] = useState([]);
    const [currentBannerUrls, setCurrentBannerUrls] = useState([]);

    const handleImageChange = async (e, setFieldValue, currentFiles) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            setIsProfileUploading(true);
            const uploadFormData = new FormData();
            files.forEach(file => {
                uploadFormData.append("banner", file);
            });

            try {
                const uploadResponse = await dispatch(entertainmentImageUpload(uploadFormData)).unwrap();
                if (uploadResponse.IsSuccess && uploadResponse.Data) {
                    const newUrls = uploadResponse.Data;
                    const updatedFiles = [...(currentFiles || []), ...newUrls];
                    setSelectedImages(updatedFiles);
                    setCurrentBannerUrls(updatedFiles);
                    setFieldValue('files', updatedFiles);
                } else {
                    toast.error('Failed to upload images');
                }
            } catch (error) {
                toast.error('Failed to upload images');
            } finally {
                setIsProfileUploading(false);
            }
        }
    };

    const removeImage = (index, setFieldValue, currentFiles) => {
        const newImages = selectedImages.filter((_, i) => i !== index);
        const newFiles = currentFiles?.filter((_, i) => i !== index) || [];
        setSelectedImages(newImages);
        setCurrentBannerUrls(newFiles);
        setFieldValue('files', newFiles);
    };

    const handleSubmit = async (values) => {
        try {
            const bannerUrls = values.files && values.files.length > 0 ? values.files : currentBannerUrls;

            const payload = {
                banner: bannerUrls,
                fromDate: values.fromDate,
                toDate: values.toDate
            };

            if (rowData?._id) {
                payload.entertainmentid = rowData._id;
            }

            const addEditData = await dispatch(addEditEntertainment(payload)).unwrap();
            if (addEditData.IsSuccess) {
                toast.success(addEditData?.Message);
                onClose();
            } else {
                toast.error(addEditData?.Message || 'Something went wrong');
            }
        } catch (error) {
            toast.error('Failed to save banner');
        }
    }

    useEffect(() => {
    }, [selectedImages])

    useEffect(() => {
        const fetchEntertainmentData = async () => {
            if (rowData?._id) {
                try {
                    const response = await dispatch(getOneEntertainment({ entertainmentid: rowData._id })).unwrap();
                    if (response.IsSuccess && response.Data) {
                        const data = response.Data;
                        const bannerUrls = data.banner && Array.isArray(data.banner) ? data.banner : [];
                        setInitialValues({
                            banner: bannerUrls,
                            fromDate: data.fromDate || today,
                            toDate: data.toDate || "",
                            files: bannerUrls
                        });
                        setSelectedImages(bannerUrls);
                        setCurrentBannerUrls(bannerUrls);
                    }
                } catch (error) {
                    toast.error('Failed to fetch banner data');
                }
            } else {
                setInitialValues({
                    banner: [],
                    fromDate: today,
                    toDate: "",
                    files: []
                });
                setSelectedImages([]);
                setCurrentBannerUrls([]);
            }
        };

        if (isOpen) {
            fetchEntertainmentData();
        }
    }, [isOpen, rowData, dispatch, today]);

    if (!isOpen) return null;
    return (
        <>
            <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="fixed inset-0 bg-black opacity-50 backdrop-blur-lg overflow-hidden"></div>
                <div className="flex items-center justify-center min-h-screen p-4">
                    <div className="relative bg-white rounded-3xl shadow-xl max-w-4xl w-full p-3 md:p-7">
                        <div className="flex items-center justify-between p-4">
                            <h3 className="text-24 lg:text-36 font-bold text-text_1">{rowData?._id ? 'Edit Banner' : 'Add Banner'}</h3>
                            <button
                                onClick={onClose}
                                className="text-text_1"
                            >
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <Formik
                            initialValues={initialValues}
                            onSubmit={handleSubmit}
                            enableReinitialize={true}
                        >
                            {({ isSubmitting, setFieldValue, values }) => (
                                <div className="lg:p-4 p-2.5">
                                    <Form>
                                        <div>
                                            <label className="block text-16 font-bold text-text_2 mb-2">
                                                Upload photo
                                            </label>
                                            <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-4">
                                                <div className="w-full min-h-[100px]">
                                                    {isProfileUploading && (
                                                        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white bg-opacity-70 rounded-lg">
                                                            <div className="flex flex-col items-center">
                                                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                                                                <span className="mt-2 text-sm font-semibold text-primary">Uploading...</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {selectedImages.length > 0 ? (
                                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                                            {selectedImages.map((image, index) => (
                                                                <div key={index} className="relative group">
                                                                    <img
                                                                        src={image}
                                                                        alt={`Preview ${index + 1}`}
                                                                        className="w-full h-[100px] object-cover rounded-lg"
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            removeImage(index, setFieldValue, values.files);
                                                                        }}
                                                                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center z-10"
                                                                    >
                                                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                        </svg>
                                                                    </button>
                                                                </div>
                                                            ))}
                                                            <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg h-[100px] bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer">
                                                                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                                </svg>
                                                                <span className="text-12 text-gray-500 mt-1">Add More</span>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="h-[100px] flex items-center justify-center">
                                                            <div className="text-center">
                                                                <div className="flex justify-center">
                                                                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <path d="M11.9999 14.3333C9.97325 14.3333 8.33325 12.6933 8.33325 10.6667C8.33325 8.64 9.97325 7 11.9999 7C14.0266 7 15.6666 8.64 15.6666 10.6667C15.6666 12.6933 14.0266 14.3333 11.9999 14.3333ZM11.9999 9C11.5579 9 11.134 9.17559 10.8214 9.48816C10.5088 9.80072 10.3333 10.2246 10.3333 10.6667C10.3333 11.1087 10.5088 11.5326 10.8214 11.8452C11.134 12.1577 11.5579 12.3333 11.9999 12.3333C12.4419 12.3333 12.8659 12.1577 13.1784 11.8452C13.491 11.5326 13.6666 11.1087 13.6666 10.6667C13.6666 10.2246 13.491 9.80072 13.1784 9.48816C12.8659 9.17559 12.4419 9 11.9999 9Z" fill="#9BA0A8" />
                                                                        <path d="M20 30.3334H12C4.75996 30.3334 1.66663 27.2401 1.66663 20.0001V12.0001C1.66663 4.76008 4.75996 1.66675 12 1.66675H17.3333C17.88 1.66675 18.3333 2.12008 18.3333 2.66675C18.3333 3.21341 17.88 3.66675 17.3333 3.66675H12C5.85329 3.66675 3.66663 5.85341 3.66663 12.0001V20.0001C3.66663 26.1467 5.85329 28.3334 12 28.3334H20C26.1466 28.3334 28.3333 26.1467 28.3333 20.0001V13.3334C28.3333 12.7867 28.7866 12.3334 29.3333 12.3334C29.88 12.3334 30.3333 12.7867 30.3333 13.3334V20.0001C30.3333 27.2401 27.24 30.3334 20 30.3334Z" fill="#9BA0A8" />
                                                                        <path d="M24 11.6667C23.4533 11.6667 23 11.2133 23 10.6667V2.66667C23 2.26667 23.24 1.89333 23.6133 1.74667C23.9867 1.6 24.4133 1.68 24.7067 1.96L27.3733 4.62667C27.76 5.01333 27.76 5.65333 27.3733 6.04C26.9867 6.42667 26.3467 6.42667 25.96 6.04L25 5.08V10.6667C25 11.2133 24.5467 11.6667 24 11.6667Z" fill="#9BA0A8" />
                                                                        <path d="M21.3333 6.3335C21.0799 6.3335 20.8266 6.24017 20.6266 6.04017C20.4406 5.85198 20.3363 5.59807 20.3363 5.3335C20.3363 5.06893 20.4406 4.81502 20.6266 4.62683L23.2932 1.96017C23.6799 1.5735 24.3199 1.5735 24.7066 1.96017C25.0933 2.34683 25.0933 2.98683 24.7066 3.3735L22.0399 6.04017C21.8399 6.24017 21.5866 6.3335 21.3333 6.3335ZM3.55992 26.2668C3.3468 26.2654 3.13971 26.1959 2.96878 26.0686C2.79785 25.9413 2.672 25.7628 2.60953 25.559C2.54706 25.3553 2.55124 25.1369 2.62145 24.9356C2.69166 24.7344 2.82425 24.5608 2.99992 24.4402L9.57325 20.0268C11.0133 19.0668 12.9999 19.1735 14.3066 20.2802L14.7466 20.6668C15.4132 21.2402 16.5466 21.2402 17.1999 20.6668L22.7466 15.9068C24.1599 14.6935 26.3866 14.6935 27.8133 15.9068L29.9866 17.7735C30.3999 18.1335 30.4532 18.7602 30.0933 19.1868C29.7332 19.6002 29.0933 19.6535 28.6799 19.2935L26.5066 17.4268C25.8399 16.8535 24.7199 16.8535 24.0533 17.4268L18.5066 22.1868C17.0933 23.4002 14.8666 23.4002 13.4399 22.1868L12.9999 21.8002C12.3866 21.2802 11.3733 21.2268 10.6933 21.6935L4.13325 26.1068C3.94658 26.2135 3.74658 26.2668 3.55992 26.2668Z" fill="#9BA0A8" />
                                                                    </svg>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                <input
                                                    type="file"
                                                    multiple
                                                    disabled={isProfileUploading}
                                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                                                    accept="image/*"
                                                    onChange={(e) => handleImageChange(e, setFieldValue, values.files)}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap -mx-3">
                                            <div className="w-full md:w-1/2 px-3 py-3">
                                                <label className="block text-16 font-bold text-text_2 mb-2">
                                                    From Date
                                                </label>
                                                <Field
                                                    type="date"
                                                    name="fromDate"
                                                    className="w-full px-3 py-3.5 bg-[#EEEEEE] rounded-md"
                                                />
                                            </div>
                                            <div className="w-full md:w-1/2 px-3 py-3">
                                                <label className="block text-16 font-bold text-text_2 mb-2">
                                                    To Date
                                                </label>
                                                <Field
                                                    type="date"
                                                    name="toDate"
                                                    className="w-full px-3 py-3.5 bg-[#EEEEEE] rounded-md"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-center p-4 space-x-3 xl:space-x-5">
                                            <button
                                                type="button"
                                                onClick={onClose}
                                                className="btn_secondary">
                                                Cancel
                                            </button>
                                            <button type="submit"
                                                disabled={isSubmitting || isProfileUploading}
                                                className={`btn py-2.5 md:py-3.5 px-8 md:px-12 ${(isSubmitting || isProfileUploading) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                                {isSubmitting ? "Submitting..." : "Save"}
                                            </button>
                                        </div>
                                    </Form>
                                </div>
                            )}
                        </Formik>

                    </div>
                </div>
            </div>
        </>
    );
};

AddEditEntertainment.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    rowData: PropTypes.object,
    getEntertianmentListFun: PropTypes.func,
    children: PropTypes.any,
};

export default AddEditEntertainment;